import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Select from "react-select";
import AlertSubscribe from "../../../../components/Notifications/AlertSubscribe";
import httpClient from "../../../../utility/httpClient.util";
import { getMedicationServiceStatus } from "utility/medicationServiceStatus.util";

const FLOATING_DRAWER_TOP = 56;
const SCREENING_DASHBOARD_PATH = "/clinical/patient-visit/screening-dashboard";
const PATIENT_VISIT_PATH = "/clinical/patient-visit/findByVn";
const PLACE_PATH = "/core/place";
const EXAM_ROOM_STORAGE_KEY = "doctorExamRoomRegister";
const PER_PAGE = 10;
const PATIENT_INCOMING_EVENT = "patient-incoming";
const PATIENT_INCOMING_ROOM_PREFIX = "place-";
const DOCTOR_EXAM_STAGE_CODE = "OPD_DOCTOR_EXAM";
const DOCTOR_EXAM_OVERDUE_SECONDS = 24 * 60 * 60;

const TAB_KEYS = {
  waiting: "waiting",
  done: "done",
  history: "history",
};

const tabList = [
  { key: TAB_KEYS.waiting, label: "รอตรวจ" },
  { key: TAB_KEYS.done, label: "ตรวจแล้ว" },
  { key: TAB_KEYS.history, label: "ประวัติ" },
];

const rootStyle = {
  position: "fixed",
  top: FLOATING_DRAWER_TOP,
  right: 0,
  bottom: 0,
  zIndex: 1031,
  pointerEvents: "none",
};

const getPanelStyle = (isOpen) => ({
  position: "relative",
  width: "min(440px, calc(100vw - 54px))",
  height: "calc(100vh - " + FLOATING_DRAWER_TOP + "px)",
  backgroundColor: "#f8fafc",
  borderLeft: "1px solid #e2e8f0",
  boxShadow: "0 16px 40px rgba(15, 23, 42, 0.22)",
  transform: isOpen ? "translateX(0)" : "translateX(100%)",
  transition: "transform 220ms ease",
  pointerEvents: "auto",
});

const getHandleStyle = () => ({
  position: "absolute",
  top: 220,
  left: -74,
  width: 74,
  height: 88,
  border: 0,
  borderRadius: "44px 0 0 44px",
  background: "linear-gradient(135deg, #0f766e 0%, #14b8a6 48%, #22c55e 100%)",
  color: "#fff",
  boxShadow: "0 10px 24px rgba(20, 184, 166, 0.32)",
  cursor: "pointer",
  outline: "none",
  padding: 0,
});

const handleContentStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  lineHeight: 1,
};

const handleCountStyle = {
  position: "absolute",
  top: 10,
  right: 9,
  minWidth: 18,
  height: 18,
  padding: "0 5px",
  borderRadius: 9,
  backgroundColor: "#f97316",
  color: "#fff",
  fontSize: 11,
  fontWeight: 700,
  lineHeight: "18px",
};

const handleLabelStyle = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0,
  marginTop: 6,
};

const panelBodyStyle = {
  height: "100%",
  overflowY: "auto",
  overflowX: "hidden",
};

const headerStyle = {
  padding: "18px 18px 14px",
  background: "linear-gradient(135deg, #0f172a 0%, #155e75 58%, #0f766e 100%)",
  color: "#fff",
};

const titleRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
};

const titleStyle = {
  fontSize: 18,
  fontWeight: 700,
  margin: 0,
};

const subtitleStyle = {
  margin: "6px 0 0",
  color: "rgba(255,255,255,0.78)",
  fontSize: 12,
};

const closeButtonStyle = {
  width: 32,
  height: 32,
  border: "1px solid rgba(255,255,255,0.22)",
  borderRadius: 6,
  color: "#fff",
  backgroundColor: "rgba(255,255,255,0.12)",
};

const contentStyle = {
  padding: 14,
};

const toolbarStyle = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 8,
  marginBottom: 12,
};

const toolbarLabelStyle = {
  margin: 0,
  color: "#475569",
  fontSize: 12,
  fontWeight: 700,
};

const tabBarStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 6,
  padding: 4,
  borderRadius: 8,
  backgroundColor: "#e2e8f0",
  marginBottom: 12,
};

const getTabStyle = (active) => ({
  border: 0,
  borderRadius: 6,
  padding: "8px 8px",
  backgroundColor: active ? "#fff" : "transparent",
  color: active ? "#0f172a" : "#475569",
  fontSize: 13,
  fontWeight: 800,
  textShadow: "none",
  boxShadow: active ? "0 1px 4px rgba(15, 23, 42, 0.12)" : "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
});

const tabLabelStyle = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const getTabCountStyle = (active) => ({
  minWidth: 20,
  height: 20,
  padding: "0 6px",
  borderRadius: 999,
  backgroundColor: active ? "#0f766e" : "#cbd5e1",
  color: active ? "#fff" : "#334155",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  fontWeight: 800,
  lineHeight: "20px",
});

const searchInputStyle = {
  width: "100%",
  height: 38,
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "0 12px",
  marginBottom: 12,
  outline: "none",
};

const getPatientCardStyle = (active) => ({
  border: active ? "1px solid #38bdf8" : "1px solid #e2e8f0",
  borderRadius: 8,
  backgroundColor: "#fff",
  padding: 12,
  marginBottom: 10,
  boxShadow: active
    ? "0 0 0 3px rgba(14, 165, 233, 0.12), 0 8px 18px rgba(14, 165, 233, 0.14)"
    : "0 1px 8px rgba(15, 23, 42, 0.05)",
});

const patientHeaderStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 10,
};

const queueNoStyle = {
  minWidth: 38,
  height: 38,
  borderRadius: 8,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#eff6ff",
  color: "#2563eb",
  fontWeight: 800,
  fontSize: 16,
};

const patientNameStyle = {
  margin: 0,
  fontSize: 14,
  fontWeight: 800,
  color: "#0f172a",
};

const metaStyle = {
  marginTop: 3,
  color: "#64748b",
  fontSize: 12,
};

const badgeRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  marginTop: 10,
};

const doctorExamRowStyle = {
  alignItems: "center",
  border: "1px solid",
  borderRadius: 8,
  display: "flex",
  gap: 8,
  marginTop: 10,
  minHeight: 38,
  padding: "7px 9px",
};

const doctorExamTextStyle = {
  display: "flex",
  flex: 1,
  flexWrap: "wrap",
  gap: "3px 6px",
  minWidth: 0,
};

const doctorExamLabelStyle = {
  fontSize: 11,
  fontWeight: 800,
  lineHeight: 1.25,
};

const doctorExamValueStyle = {
  fontSize: 11,
  fontWeight: 800,
  lineHeight: 1.25,
};

const doctorExamSubTextStyle = {
  flexBasis: "100%",
  fontSize: 10,
  fontWeight: 700,
  lineHeight: 1.25,
};

const getDoctorExamRowStyle = (tone) => {
  const styles = {
    danger: { backgroundColor: "#fef2f2", borderColor: "#fecaca", color: "#dc2626" },
    info: { backgroundColor: "#eff6ff", borderColor: "#bfdbfe", color: "#2563eb" },
    success: { backgroundColor: "#f0fdf4", borderColor: "#bbf7d0", color: "#16a34a" },
  };

  return {
    ...doctorExamRowStyle,
    ...(styles[tone] || styles.info),
  };
};

const getBadgeStyle = (tone) => {
  const styles = {
    danger: { backgroundColor: "#fef2f2", color: "#dc2626", borderColor: "#fecaca" },
    warning: { backgroundColor: "#fff7ed", color: "#ea580c", borderColor: "#fed7aa" },
    success: { backgroundColor: "#f0fdf4", color: "#16a34a", borderColor: "#bbf7d0" },
    info: { backgroundColor: "#f0f9ff", color: "#0284c7", borderColor: "#bae6fd" },
    slate: { backgroundColor: "#f8fafc", color: "#475569", borderColor: "#e2e8f0" },
  };
  return {
    border: "1px solid",
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 700,
    ...(styles[tone] || styles.slate),
  };
};

const serviceStatusRowStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
  gap: 6,
  marginTop: 10,
};

const serviceStatusConfig = {
  danger: {
    backgroundColor: "#fef2f2",
    borderColor: "#fecaca",
    color: "#dc2626",
    icon: "icon-x",
  },
  none: {
    backgroundColor: "#f8fafc",
    borderColor: "#e2e8f0",
    color: "#64748b",
    icon: "icon-minus",
  },
  success: {
    backgroundColor: "#f0fdf4",
    borderColor: "#bbf7d0",
    color: "#16a34a",
    icon: "icon-check",
  },
  warning: {
    backgroundColor: "#fff7ed",
    borderColor: "#fed7aa",
    color: "#ea580c",
    icon: "icon-clock",
  },
};

const getServiceStatusStyle = (tone) => {
  const config = serviceStatusConfig[tone] || serviceStatusConfig.none;
  return {
    minWidth: 0,
    height: 36,
    border: "1px solid " + config.borderColor,
    borderRadius: 8,
    backgroundColor: config.backgroundColor,
    color: config.color,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
  };
};

const serviceStatusLabelStyle = {
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: 9,
  fontWeight: 700,
  lineHeight: 1,
};

const detailRowStyle = {
  display: "grid",
  gridTemplateColumns: "1fr auto",
  gap: 10,
  alignItems: "center",
  marginTop: 10,
};

const ccStyle = {
  color: "#334155",
  fontSize: 12,
  lineHeight: 1.35,
};

const selectButtonStyle = {
  border: 0,
  borderRadius: 6,
  padding: "7px 10px",
  backgroundColor: "#0ea5e9",
  color: "#fff",
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const emptyStyle = {
  padding: 24,
  textAlign: "center",
  color: "#64748b",
  backgroundColor: "#fff",
  border: "1px dashed #cbd5e1",
  borderRadius: 8,
};

const loadingStyle = {
  padding: 22,
  textAlign: "center",
  color: "#0f766e",
  backgroundColor: "#fff",
  border: "1px solid #dbeafe",
  borderRadius: 8,
  fontWeight: 700,
};

const errorStyle = {
  padding: 12,
  color: "#b91c1c",
  backgroundColor: "#fef2f2",
  border: "1px solid #fecaca",
  borderRadius: 8,
  marginBottom: 10,
};

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 38,
    borderColor: "#cbd5e1",
    boxShadow: "none",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 1060,
  }),
};

const getTodayDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return year + "-" + month + "-" + day;
};

const getValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "") || "";

const getResponsePayload = (response) => response?.data?.data || response?.data || response;

const normalizeList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const getResponseTotal = (payload, rows = []) =>
  Number(
    getValue(
      payload?.total,
      payload?.totalItems,
      payload?.totalCount,
      payload?.meta?.total,
      payload?.pagination?.total,
      rows.length,
    ),
  ) || rows.length;

const formatTime = (value) => {
  if (!value) return "-";
  const text = String(value);
  if (/^\d{2}:\d{2}/.test(text)) return text.slice(0, 5);
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text.slice(0, 5);
  return String(date.getHours()).padStart(2, "0") + ":" + String(date.getMinutes()).padStart(2, "0");
};

const getPersonName = (visit = {}) => {
  const person = visit.patient?.person || visit.person || {};
  const prefix = getValue(person.prefix?.name, person.prefixName, visit.prefixName);
  const firstName = getValue(person.firstName, visit.firstName);
  const lastName = getValue(person.lastName, visit.lastName);
  const composed = [prefix, firstName, lastName].filter(Boolean).join(" ");
  return getValue(visit.patientName, visit.patient?.fullName, visit.fullName, composed, "ไม่ระบุชื่อ");
};

const getSexAge = (visit = {}) => {
  const person = visit.patient?.person || visit.person || {};
  const sex = getValue(person.gender, person.sex, person.genderCode, visit.gender, visit.sex, "-");
  const age = getValue(visit.age, person.age, visit.patientAge, "");
  return age ? sex + " " + age : sex;
};

const getPlaceLabel = (visit = {}) => {
  const placeCode = getValue(visit.placeCode, visit.place?.placeCode);
  const placeName = getValue(visit.placeName, visit.place?.name, visit.place?.description);
  if (placeCode && placeName) return placeCode + " - " + placeName;
  return placeName || placeCode || "ไม่ระบุห้อง";
};

const getVisitDateTime = (visit = {}) =>
  getValue(visit.visitDateTime, visit.visitDate, visit.createdAt, visit.updatedAt);

const getWaitMinute = (visit = {}) => {
  const explicit = getValue(visit.waitMinute, visit.waitMinutes, visit.waitingMinute);
  if (explicit !== "") return explicit;
  const value = getVisitDateTime(visit);
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "-";
  return Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
};

const getDateTimeMs = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

const formatDateTimeShort = (value, nowMs = Date.now()) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const now = new Date(nowMs);
  const timeText =
    String(date.getHours()).padStart(2, "0") +
    ":" +
    String(date.getMinutes()).padStart(2, "0");
  const isSameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (isSameDay) return timeText;

  return (
    String(date.getDate()).padStart(2, "0") +
    "/" +
    String(date.getMonth() + 1).padStart(2, "0") +
    " " +
    timeText
  );
};

const getElapsedSeconds = (startedAt, endedAt, nowMs = Date.now()) => {
  const startedAtMs = getDateTimeMs(startedAt);
  if (!startedAtMs) return 0;

  const endedAtMs = getDateTimeMs(endedAt);
  const targetMs = endedAtMs || nowMs;

  return Math.max(0, Math.floor((targetMs - startedAtMs) / 1000));
};

const formatDuration = (totalSeconds) => {
  const seconds = Math.max(0, Number(totalSeconds) || 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainSeconds = seconds % 60;

  return [hours, minutes, remainSeconds]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
};

const getDoctorExamStage = (visit = {}) => {
  if (visit.doctorExamStage) return visit.doctorExamStage;
  if (visit.doctorExam) return visit.doctorExam;

  const stages = Array.isArray(visit.stages)
    ? visit.stages
    : Array.isArray(visit.patientJourneyStages)
    ? visit.patientJourneyStages
    : [];

  return (
    [...stages]
      .reverse()
      .find((stage) => stage?.stageCode === DOCTOR_EXAM_STAGE_CODE) || null
  );
};

const getDoctorExamInfo = (visit = {}) => {
  const stage = getDoctorExamStage(visit) || {};
  const startedAt = getValue(
    stage.startedAt,
    visit.doctorExamStartedAt,
    visit.doctorExamStartAt,
  );
  const endedAt = getValue(stage.endedAt, visit.doctorExamEndedAt, visit.doctorExamEndAt);
  const status = String(getValue(stage.status, visit.doctorExamStatus, "")).toUpperCase();
  const durationSeconds = getValue(stage.durationSeconds, visit.doctorExamDurationSeconds);

  if (!startedAt) return null;

  return {
    startedAt,
    endedAt,
    durationSeconds: durationSeconds === "" ? null : durationSeconds,
    isCompleted: Boolean(endedAt) || status === "COMPLETED",
    status,
  };
};

const getDoctorExamDisplay = (doctorExam, nowMs = Date.now()) => {
  if (!doctorExam?.startedAt) return null;

  const elapsedSeconds =
    doctorExam.isCompleted && doctorExam.durationSeconds !== null
      ? Number(doctorExam.durationSeconds) || 0
      : getElapsedSeconds(doctorExam.startedAt, doctorExam.endedAt, nowMs);

  if (doctorExam.isCompleted) {
    return {
      icon: "icon-check-circle",
      label: "เวลาทำงานแพทย์",
      subText:
        formatDateTimeShort(doctorExam.startedAt, nowMs) +
        " - " +
        formatDateTimeShort(doctorExam.endedAt, nowMs),
      tone: "success",
      value: formatDuration(elapsedSeconds),
    };
  }

  const isOverdue = elapsedSeconds >= DOCTOR_EXAM_OVERDUE_SECONDS;

  return {
    icon: isOverdue ? "icon-alert-circle" : "icon-clock",
    label: "เริ่มงาน " + formatDateTimeShort(doctorExam.startedAt, nowMs),
    subText: isOverdue ? "เกิน 1 วันแล้วยังไม่จบงาน" : "กำลังทำงาน",
    tone: isOverdue ? "danger" : "info",
    value: formatDuration(elapsedSeconds),
  };
};

const isUrgentVisit = (visit = {}) => {
  const priority = String(
    getValue(
      visit.patientVisitEntryPriority?.code,
      visit.patientVisitEntryPriority?.name,
      visit.priorityCode,
      visit.priorityName,
      "",
    ),
  ).toLowerCase();
  return Boolean(visit.isUrgent || priority.includes("urgent") || priority.includes("ด่วน"));
};

const isExaminedVisit = (visit = {}) => {
  const status = String(getValue(visit.examStatus, visit.status, "")).toUpperCase();
  return status === "EXAMINED" || status === "DONE" || status === "FINISHED";
};

const isTruthyStatus = (value) => {
  if (value === true) return true;
  if (Number(value) === 1) return true;
  return ["Y", "YES", "TRUE", "DONE", "COMPLETED"].includes(
    String(value || "").trim().toUpperCase(),
  );
};

const getStatusTextValue = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null || value === "") continue;
    if (typeof value === "object") {
      const nestedValue = getStatusTextValue(
        value.name,
        value.status,
        value.code,
        value.label,
        value.requestStatus,
        value.patientRequestStatus,
      );
      if (nestedValue) return nestedValue;
      continue;
    }
    return String(value);
  }
  return "";
};

const isCompletedStatusText = (statusText) => {
  const value = String(statusText || "").toLowerCase();
  return [
    "completed",
    "complete",
    "confirmed",
    "dispensed",
    "film read",
    "lis resulted",
    "paid",
    "read",
    "result",
    "ตรวจแล้ว",
    "จ่ายแล้ว",
    "สำเร็จ",
    "เสร็จ",
  ].some((keyword) => value.includes(keyword));
};

const isDangerStatusText = (statusText) => {
  const value = String(statusText || "").toLowerCase();
  return ["cancel", "error", "reject", "ยกเลิก", "ปฏิเสธ"].some((keyword) =>
    value.includes(keyword),
  );
};

const getRequestTone = (hasRequest, statusText) => {
  if (!hasRequest) return "none";
  if (isDangerStatusText(statusText)) return "danger";
  if (isCompletedStatusText(statusText)) return "success";
  return "warning";
};

const getServiceStatuses = (visit = {}, done = false) => {
  const screeningDone =
    done || isTruthyStatus(visit.isScreening) || isTruthyStatus(visit.checkScreening);
  const labStatusText = getStatusTextValue(
    visit.labStatus,
    visit.labRequestStatus,
    visit.patientRequestStatus,
  );
  const radioStatusText = getStatusTextValue(
    visit.radioStatus,
    visit.radiologyStatus,
    visit.radiologyResult,
  );
  const rxStatus = getMedicationServiceStatus(visit);
  const financeDone = isTruthyStatus(visit.isFinanceDone);

  return [
    {
      key: "screening",
      label: "Scr",
      title: screeningDone ? "Screening: done" : "Screening: no data",
      tone: screeningDone ? "success" : "none",
    },
    {
      key: "lab",
      label: "Lab",
      title: labStatusText ? "Lab: " + labStatusText : "Lab: no request",
      tone: getRequestTone(
        isTruthyStatus(visit.isLabStatus) || Boolean(labStatusText),
        labStatusText,
      ),
    },
    {
      key: "radiology",
      label: "Xray",
      title: radioStatusText ? "Radiology: " + radioStatusText : "Radiology: no request",
      tone: getRequestTone(
        isTruthyStatus(visit.isRadioStatus) || Boolean(radioStatusText),
        radioStatusText,
      ),
    },
    {
      key: "rx",
      label: "Rx",
      title:
        rxStatus === "done"
          ? "จ่ายยาแล้ว"
          : rxStatus === "waiting"
            ? "มีรายการยา / รอจ่ายยา"
            : "ไม่มีข้อมูลจ่ายยา",
      tone:
        rxStatus === "done"
          ? "success"
          : rxStatus === "waiting"
            ? "warning"
            : "none",
    },
    {
      key: "finance",
      label: "Pay",
      title: financeDone ? "การเงินเรียบร้อยแล้ว" : "ยังไม่มีสถานะการเงิน",
      tone: financeDone ? "success" : "none",
    },
  ];
};

const mapDashboardVisit = (visit = {}, index = 0) => {
  const done = isExaminedVisit(visit);
  const queueNo = getValue(visit.queueNo, visit.queue, visit.seq, visit.no, index + 1);
  const diagnosis = getValue(
    visit.diagnosis,
    visit.diagnosisText,
    visit.doctorDiagnosis,
    visit.icd10Text,
    "รอตรวจสรุป",
  );

  return {
    raw: visit,
    queueNo,
    urgent: isUrgentVisit(visit),
    hn: getValue(visit.hn, visit.patient?.hn, visit.patientHn),
    vn: getValue(visit.vn, visit.visitNo, visit.patientVisit?.vn),
    name: getPersonName(visit),
    sexAge: getSexAge(visit),
    arriveTime: formatTime(getValue(visit.visitTime, visit.visitDateTime, visit.createdAt)),
    doneTime: formatTime(
      getValue(
        visit.doctorExamEndedAt,
        visit.doctorExamStage?.endedAt,
        visit.examinedAt,
        visit.doneAt,
        visit.updatedAt,
      ),
    ),
    waitMinute: getWaitMinute(visit),
    room: getPlaceLabel(visit),
    cc: getValue(
      visit.chiefComplaint,
      visit.cc,
      visit.screeningChiefComplaint,
      visit.symptom,
      visit.visitReason,
      "ไม่มี CC",
    ),
    flag: isUrgentVisit(visit) ? "ด่วน" : getValue(visit.flag, visit.priorityName, "ทั่วไป"),
    diagnosis,
    placeCode: getValue(visit.placeCode, visit.place?.placeCode),
    serviceStatuses: getServiceStatuses(visit, done),
    doctorExam: getDoctorExamInfo(visit),
    statusText: done ? getValue(visit.statusText, "ตรวจแล้ว") : getValue(visit.statusText, "รอตรวจ"),
  };
};

const createPlaceOption = (place = {}) => {
  const value = getValue(place.placeCode, place.code, place.id);
  const name = getValue(place.name, place.placeName, place.shortName, place.description);
  return value
    ? {
        label: name ? value + " - " + name : value,
        value,
      }
    : null;
};

const readStoredExamRoom = () => {
  if (typeof window === "undefined") return null;
  const raw = window.sessionStorage.getItem(EXAM_ROOM_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "string") return { label: parsed, value: parsed };
    const value = getValue(parsed.placeCode, parsed.value, parsed.code);
    return value ? { label: getValue(parsed.label, value), value } : null;
  } catch {
    return { label: raw, value: raw };
  }
};

const writeStoredExamRoom = (option) => {
  if (typeof window === "undefined" || !option?.value) return;
  window.sessionStorage.setItem(
    EXAM_ROOM_STORAGE_KEY,
    JSON.stringify({
      label: option.label,
      placeCode: option.value,
      updatedAt: new Date().toISOString(),
    }),
  );
};

const setQueryParam = (params, key, value) => {
  if (value !== undefined && value !== null && value !== "") {
    params.set(key, value);
    return;
  }
  params.delete(key);
};

const getPatientIncomingRoom = (placeCode) => {
  const value = String(placeCode || "").trim();
  if (!value) return "";
  return value.startsWith(PATIENT_INCOMING_ROOM_PREFIX)
    ? value
    : PATIENT_INCOMING_ROOM_PREFIX + value;
};

const PatientCard = ({ active, nowMs, patient, done, onSelect }) => {
  const urgentTone = patient.urgent ? "danger" : "slate";
  const doctorExamDisplay = getDoctorExamDisplay(patient.doctorExam, nowMs);
  return (
    <div style={getPatientCardStyle(active)}>
      <div style={patientHeaderStyle}>
        <div style={{ display: "flex", gap: 10, minWidth: 0 }}>
          <span style={queueNoStyle}>{patient.queueNo}</span>
          <div style={{ minWidth: 0 }}>
            <p style={patientNameStyle}>{patient.name}</p>
            <div style={metaStyle}>
              HN {patient.hn || "-"} · {patient.sexAge} · {patient.arriveTime}
            </div>
          </div>
        </div>
        <span style={getBadgeStyle(active ? "info" : done ? "success" : urgentTone)}>
          {active ? "กำลังดู" : done ? patient.statusText : patient.flag}
        </span>
      </div>

      <div style={badgeRowStyle}>
        <span style={getBadgeStyle("info")}>{patient.room}</span>
        {!done && <span style={getBadgeStyle("warning")}>รอ {patient.waitMinute} นาที</span>}
        {done && <span style={getBadgeStyle("slate")}>เสร็จ {patient.doneTime}</span>}
      </div>

      {doctorExamDisplay && (
        <div style={getDoctorExamRowStyle(doctorExamDisplay.tone)}>
          <i className={"feather " + doctorExamDisplay.icon} style={{ fontSize: 15 }} />
          <div style={doctorExamTextStyle}>
            <span style={doctorExamLabelStyle}>{doctorExamDisplay.label}</span>
            <span style={doctorExamValueStyle}>{doctorExamDisplay.value}</span>
            <span style={doctorExamSubTextStyle}>{doctorExamDisplay.subText}</span>
          </div>
        </div>
      )}

      <div style={serviceStatusRowStyle}>
        {(patient.serviceStatuses || []).map((status) => {
          const config = serviceStatusConfig[status.tone] || serviceStatusConfig.none;
          return (
            <span key={status.key} style={getServiceStatusStyle(status.tone)} title={status.title}>
              <i className={"feather " + config.icon} style={{ fontSize: 13 }} />
              <span style={serviceStatusLabelStyle}>{status.label}</span>
            </span>
          );
        })}
      </div>

      <div style={detailRowStyle}>
        <div style={ccStyle}>{done ? patient.diagnosis : patient.cc}</div>
        <button type="button" style={selectButtonStyle} onClick={() => onSelect(patient)}>
          เปิดดู
        </button>
      </div>
    </div>
  );
};

const PatientQueueFloatingMenu = ({ defaultOpen = false }) => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search],
  );
  const visitDate = queryParams.get("visitDate") || getTodayDate();
  const currentHn = queryParams.get("hn");
  const currentVn = queryParams.get("vn");
  const queryPlaceCode = queryParams.get("placeCode");

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [activeTab, setActiveTab] = useState(TAB_KEYS.waiting);
  const [keyword, setKeyword] = useState("");
  const [placeOptions, setPlaceOptions] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(() => readStoredExamRoom());
  const [queueState, setQueueState] = useState({
    done: [],
    history: [],
    waiting: [],
  });
  const [queueTotals, setQueueTotals] = useState({
    done: 0,
    history: 0,
    waiting: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [counterTick, setCounterTick] = useState(Date.now());

  useEffect(() => {
    let isActive = true;

    const loadPlaces = async () => {
      try {
        const response = await httpClient.get(PLACE_PATH);
        const payload = getResponsePayload(response);
        const options = normalizeList(payload).map(createPlaceOption).filter(Boolean);
        if (isActive) setPlaceOptions(options);
      } catch {
        if (isActive) setPlaceOptions([]);
      }
    };

    loadPlaces();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedPlace?.value || placeOptions.length === 0) return;
    const found = placeOptions.find((item) => item.value === selectedPlace.value);
    if (found && found.label !== selectedPlace.label) setSelectedPlace(found);
  }, [placeOptions, selectedPlace]);

  useEffect(() => {
    if (!isOpen) return undefined;

    setCounterTick(Date.now());
    const intervalId = window.setInterval(() => {
      setCounterTick(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isOpen]);

  useEffect(() => {
    if (selectedPlace?.value) return;
    if (queryPlaceCode) {
      setSelectedPlace({ label: queryPlaceCode, value: queryPlaceCode });
      return;
    }
    if (!currentVn) return;

    let isActive = true;

    const loadCurrentVisitPlace = async () => {
      try {
        const response = await httpClient.get(PATIENT_VISIT_PATH, {
          params: {
            include: "place",
            vn: currentVn,
          },
        });
        const payload = getResponsePayload(response);
        const visit = Array.isArray(payload) ? payload[0] || {} : payload || {};
        const placeCode = getValue(visit.placeCode, visit.place?.placeCode);
        const placeName = getValue(visit.placeName, visit.place?.name);
        if (isActive && placeCode) {
          setSelectedPlace({
            label: placeName ? placeCode + " - " + placeName : placeCode,
            value: placeCode,
          });
        }
      } catch {}
    };

    loadCurrentVisitPlace();

    return () => {
      isActive = false;
    };
  }, [currentVn, queryPlaceCode, selectedPlace]);

  useEffect(() => {
    let isActive = true;
    const placeCode = selectedPlace?.value || "";
    const needsPlace = activeTab === TAB_KEYS.waiting || activeTab === TAB_KEYS.history;

    if (needsPlace && !placeCode) {
      setQueueState((prev) => ({ ...prev, [activeTab]: [] }));
      setQueueTotals((prev) => ({ ...prev, [activeTab]: 0 }));
      setErrorMessage("");
      return () => {
        isActive = false;
      };
    }

    const loadQueue = async () => {
      setIsLoading(true);
      setErrorMessage("");

      try {
        const params = {
          page: 1,
          perPage: PER_PAGE,
          visitDate,
        };

        if (activeTab === TAB_KEYS.waiting) {
          params.examStatus = "PENDING";
          params.placeCode = placeCode;
        } else if (activeTab === TAB_KEYS.done) {
          params.examStatus = "EXAMINED";
        } else if (activeTab === TAB_KEYS.history) {
          params.placeCode = placeCode;
        }

        const response = await httpClient.get(SCREENING_DASHBOARD_PATH, { params });
        const payload = getResponsePayload(response);
        const rows = normalizeList(payload).map(mapDashboardVisit);
        const total = getResponseTotal(payload, rows);

        if (!isActive) return;
        setQueueState((prev) => ({ ...prev, [activeTab]: rows }));
        setQueueTotals((prev) => ({ ...prev, [activeTab]: total }));
      } catch (error) {
        if (!isActive) return;
        setQueueState((prev) => ({ ...prev, [activeTab]: [] }));
        setQueueTotals((prev) => ({ ...prev, [activeTab]: 0 }));
        setErrorMessage(
          error?.response?.data?.message || error?.message || "โหลดคิวคนไข้ไม่สำเร็จ",
        );
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    loadQueue();

    return () => {
      isActive = false;
    };
  }, [activeTab, selectedPlace, visitDate]);

  const loadPendingQueue = useCallback(async () => {
    const placeCode = selectedPlace?.value || "";
    if (!placeCode) return;

    try {
      const response = await httpClient.get(SCREENING_DASHBOARD_PATH, {
        params: {
          examStatus: "PENDING",
          page: 1,
          perPage: PER_PAGE,
          placeCode,
          visitDate,
        },
      });
      const payload = getResponsePayload(response);
      const rows = normalizeList(payload).map(mapDashboardVisit);
      const total = getResponseTotal(payload, rows);

      setQueueState((prev) => ({ ...prev, waiting: rows }));
      setQueueTotals((prev) => ({ ...prev, waiting: total }));
    } catch (error) {}
  }, [selectedPlace?.value, visitDate]);

  const onPatientIncoming = useCallback(() => {
    setActiveTab(TAB_KEYS.waiting);
    loadPendingQueue();
  }, [loadPendingQueue]);

  const currentList = queueState[activeTab] || [];
  const filteredList = useMemo(() => {
    const value = keyword.trim().toLowerCase();
    if (!value) return currentList;
    return currentList.filter((patient) =>
      [patient.hn, patient.vn, patient.name, patient.cc, patient.diagnosis]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(value),
    );
  }, [currentList, keyword]);

  const onChangePlace = (option) => {
    setSelectedPlace(option);
    if (option?.value) writeStoredExamRoom(option);
  };

  const onSelectPatient = (patient) => {
    const nextParams = new URLSearchParams(location.search || "");
    setQueryParam(nextParams, "hn", patient.hn);
    setQueryParam(nextParams, "vn", patient.vn);
    nextParams.delete("visitDate");

    history.replace({
      pathname: location.pathname,
      search: nextParams.toString() ? "?" + nextParams.toString() : "",
    });
    setIsOpen(false);
  };

  const waitingCount = queueTotals.waiting || queueState.waiting.length;
  const doneCount = queueTotals.done || queueState.done.length;
  const historyCount = queueTotals.history || queueState.history.length;
  const tabCounts = {
    [TAB_KEYS.waiting]: waitingCount,
    [TAB_KEYS.done]: doneCount,
    [TAB_KEYS.history]: historyCount,
  };
  const activeTabNeedsPlace = activeTab === TAB_KEYS.waiting || activeTab === TAB_KEYS.history;
  const patientIncomingRoom = getPatientIncomingRoom(selectedPlace?.value);

  return (
    <div style={rootStyle}>
      {patientIncomingRoom && (
        <AlertSubscribe
          event={PATIENT_INCOMING_EVENT}
          onNotify={onPatientIncoming}
          room={patientIncomingRoom}
          silent={!isOpen}
        />
      )}
      <div style={getPanelStyle(isOpen)}>
        <button
          type="button"
          aria-label={isOpen ? "Close patient queue" : "Open patient queue"}
          title={isOpen ? "ปิดคิวคนไข้" : "เปิดคิวคนไข้"}
          style={getHandleStyle()}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span style={handleCountStyle}>{waitingCount}</span>
          <span style={handleContentStyle}>
            <i className="feather icon-users" style={{ fontSize: 22 }} />
            <span style={handleLabelStyle}>Queue</span>
            <i
              className={"feather " + (isOpen ? "icon-chevrons-right" : "icon-chevrons-left")}
              style={{ fontSize: 21, marginTop: 8 }}
            />
          </span>
        </button>

        <div style={panelBodyStyle}>
          <div style={headerStyle}>
            <div style={titleRowStyle}>
              <div>
                <h5 style={titleStyle}>คิวคนไข้จุดบริการ</h5>
                <p style={subtitleStyle}>visitDate {visitDate} · OPD doctor workspace</p>
              </div>
              <button type="button" style={closeButtonStyle} onClick={() => setIsOpen(false)}>
                <i className="feather icon-x" />
              </button>
            </div>

          </div>

          <div style={contentStyle}>
            <div style={toolbarStyle}>
              <p style={toolbarLabelStyle}>จุดบริการ</p>
              <Select
                isClearable={false}
                menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                noOptionsMessage={() => "ไม่พบจุดบริการ"}
                onChange={onChangePlace}
                options={placeOptions}
                placeholder="เลือกจุดบริการ"
                styles={selectStyles}
                value={selectedPlace}
              />
            </div>

            <div style={tabBarStyle}>
              {tabList.map((tab) => {
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    style={getTabStyle(active)}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    <span style={tabLabelStyle}>{tab.label}</span>
                    <span style={getTabCountStyle(active)}>{tabCounts[tab.key] || 0}</span>
                  </button>
                );
              })}
            </div>

            <input
              type="search"
              value={keyword}
              placeholder="ค้นหา HN, ชื่อ, อาการ"
              style={searchInputStyle}
              onChange={(event) => setKeyword(event.target.value)}
            />

            {errorMessage && <div style={errorStyle}>{errorMessage}</div>}
            {activeTabNeedsPlace && !selectedPlace?.value ? (
              <div style={emptyStyle}>กรุณาเลือกจุดบริการก่อน</div>
            ) : isLoading ? (
              <div style={loadingStyle}>กำลังโหลดคิว...</div>
            ) : filteredList.length === 0 ? (
              <div style={emptyStyle}>ไม่พบคิวคนไข้</div>
            ) : (
              filteredList.map((patient) => (
                <PatientCard
                  key={patient.vn || patient.hn + patient.queueNo}
                  active={patient.vn === currentVn || patient.hn === currentHn}
                  nowMs={counterTick}
                  patient={patient}
                  done={activeTab === TAB_KEYS.done || isExaminedVisit(patient.raw)}
                  onSelect={onSelectPatient}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientQueueFloatingMenu;
